import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useParent } from '../../context/ParentContext';
import { availableTeams, accountTeamRoleLabel, contextKey } from '../../lib/accountContexts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Check, Users } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountTeamsCard() {
  const { user, teams, activeTeam, changeActiveTeam, teamSwitching } = useAuth();
  const { contexts, active, setActiveContext, clearActiveContext, loading, error, refresh, switching, isParentView, leaderWorkspace } = useParent();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const visibleTeams = availableTeams(teams);
  const teamIds = new Set(visibleTeams.map(team => team.id));
  const disabled = busy || teamSwitching || switching;

  const choose = async (teamId, link = null) => {
    if (disabled) return;
    setBusy(true);
    try {
      if (link) await setActiveContext({ playerProfileId: link.player_profile_id, teamId });
      else {
        await changeActiveTeam(teamId);
        clearActiveContext();
      }
      navigate('/oversikt', { replace: true });
    } catch (err) { toast.error(err?.message || 'Kunde inte byta lag.'); }
    finally { setBusy(false); }
  };


  return (
    <>
      <Card className="bg-card border-border" data-testid="account-teams-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Users className="h-5 w-5" />Mina lag och barn</CardTitle>
          <p className="text-sm text-muted-foreground">Samma konto kan användas i flera lag. Din roll gäller för respektive lag.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Mina lag</h3>
            {!visibleTeams.length && <p className="text-sm text-muted-foreground">Inga lag att visa.</p>}
            {visibleTeams.map(team => (
              <button type="button" key={team.id} onClick={() => choose(team.id)} disabled={disabled}
                className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/20 p-3 text-left hover:bg-muted/40 disabled:opacity-50"
                data-testid={`account-team-${team.id}`}>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{team.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{accountTeamRoleLabel(user, team)}{team.organization_name ? ` · ${team.organization_name}` : ''}</span>
                </span>
                {activeTeam?.id === team.id && !isParentView && <span className="flex items-center gap-1 text-xs text-primary"><Check className="h-4 w-4" />Valt</span>}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{leaderWorkspace ? 'Föräldrakopplingar' : 'Mina barn'}</h3>
            {leaderWorkspace && <p className="text-xs text-muted-foreground">Kopplingen finns för familjefunktioner och ändrar inte ditt ledarläge.</p>}
            {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
            {error && <Button variant="outline" onClick={refresh} disabled={loading}>{error}</Button>}
            {!loading && !error && !contexts.length && <p className="text-sm text-muted-foreground">Inga barn är kopplade till ditt konto.</p>}
            {contexts.map(link => {
              const key = contextKey(link.player_profile_id, link.team_id);
              const selected = active && key === contextKey(active.player_profile_id, active.team_id);
              const available = teamIds.has(link.team_id);
              if (leaderWorkspace) return (
                <div key={key} className="rounded-lg border border-border bg-muted/20 p-3" data-testid={`account-child-${key}`}>
                  <p className="text-sm font-medium break-words">{link.player_name || 'Barn'}</p>
                  <p className="text-xs text-muted-foreground break-words">{link.team_name || 'Lag'} · Föräldrakoppling{!available && ' · Inte tillgängligt'}</p>
                </div>
              );
              return (
                <button type="button" key={key} onClick={() => choose(link.team_id, link)} disabled={disabled || loading || !available}
                  className="flex w-full items-center gap-3 rounded-lg border border-border bg-muted/20 p-3 text-left hover:bg-muted/40 disabled:opacity-50"
                  data-testid={`account-child-${key}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{link.player_name || 'Barn'}</span>
                    <span className="block truncate text-xs text-muted-foreground">{link.team_name || 'Lag'}{!available && ' · Inte tillgängligt'}</span>
                  </span>
                  {selected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                </button>
              );
            })}
          </div>
          <Button variant="outline" onClick={() => navigate('/join')} disabled={disabled}>Anslut till ett lag med kod</Button>
        </CardContent>
      </Card>

    </>
  );
}

export function AccountSecurityCard() {
  const { logoutAllDevices, teamSwitching } = useAuth();
  const { switching } = useParent();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const disabled = busy || teamSwitching || switching;
  const endAllSessions = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (await logoutAllDevices()) navigate('/login', { replace: true });
    } catch (err) { toast.error(err?.message || 'Kunde inte logga ut alla enheter.'); }
    finally { setBusy(false); }
  };

  return (
      <Card className="bg-card border-border" data-testid="account-sessions-card">
        <CardHeader><CardTitle className="font-heading">Säkerhet</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">Avsluta alla inloggningar för ditt konto, inklusive den här enheten.</p>
          {confirmLogout ? (
            <div className="space-y-3" role="group" aria-label="Bekräfta utloggning">
              <p className="text-sm">Vill du logga ut på alla enheter?</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="destructive" disabled={disabled} onClick={endAllSessions}>{busy ? 'Loggar ut…' : 'Ja, logga ut alla'}</Button>
                <Button variant="outline" disabled={disabled} onClick={() => setConfirmLogout(false)}>Avbryt</Button>
              </div>
            </div>
          ) : <Button variant="outline" disabled={disabled} onClick={() => setConfirmLogout(true)}>Logga ut alla enheter</Button>}
        </CardContent>
      </Card>
  );
}
